import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import { QUANTITY_INPUT } from "./quantityInput";

/**
 * An optional date that a source may legitimately leave blank. A source's
 * "no value" markers (empty string, zero-dates like "0001-01-01") must be
 * mapped to null upstream — this step takes a date to mean a date.
 */
const OPTIONAL_DATE_INPUT = z.string().nullable().optional();

/**
 * Upsert Inventory Lot
 *
 * Takes ONE lot's fields as single-value input sockets, shaped after the Gently
 * model (`im_inventory_lots`) — not after any source system. A CDC observer or
 * file parser maps its own columns into these sockets, so the same step serves
 * every source; anything source-specific (field naming, sentinel values, code
 * decoding) belongs in that upstream mapping.
 *
 * A lot is per-warehouse, per-product, per-unit, per-day: the conflict target is
 * `(warehouse, product, default variant, unitNumber, snapshotDate)`. Re-running
 * a day's extract updates that day's rows in place rather than appending.
 *
 * The warehouse resolves from its origin code and the product from its SKU
 * (optionally scoped by the `originSystem` config); rows referencing unknown
 * warehouses or products are skipped and reported per input, never
 * auto-created — the product catalog and warehouse seed remain the source of
 * truth. With a `batch` config the engine hands whole chunks to the bulk
 * handler. A leaf step: per-row outputs are execution results, not a
 * downstream data contract.
 */
export const upsertInventoryLotStep = defineStepConfig({
  type: "upsert_inventory_lot",
  name: "Upsert Inventory Lot",
  description:
    "Upserts one inventory lot (warehouse code, product SKU, unit number, snapshot date, quantity, optional expiration/created dates) into im_inventory_lots. Unknown warehouses/products are skipped and reported, never created.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    productSku: z.string().describe("Product SKU (resolved to the product)"),
    warehouseCode: z
      .string()
      .describe("Warehouse origin code (resolved to the warehouse)"),
    unitNumber: z
      .string()
      .describe("Lot / unit identifier, unique within the warehouse+product"),
    snapshotDate: z.string().describe("Date this lot quantity was observed"),
    quantity: QUANTITY_INPUT.describe("Quantity in this lot"),
    expirationDate: OPTIONAL_DATE_INPUT.describe(
      "Lot expiration date, or null when the source has none",
    ),
    lotCreatedDate: OPTIONAL_DATE_INPUT.describe(
      "Date the lot was created in the source, or null",
    ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The lot was upserted"),
    skipped: z
      .boolean()
      .describe("The row was skipped because a reference was unresolved"),
    unresolvedProduct: z
      .boolean()
      .describe(
        "No product matched the SKU (ingest the product catalog first)",
      ),
    unresolvedWarehouse: z
      .boolean()
      .describe("No warehouse matched the warehouse code"),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    originSystem: z
      .string()
      .optional()
      .describe(
        "Restrict WAREHOUSE resolution to references ingested from this origin system. Does not scope products: ProductV3 is keyed by SKU within the organization and carries no origin system. Leave empty to match any.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
